<template>
  <div>
    <!-- Sample Download Button -->
    <button
        class="btn btn-secondary excel-download-btn"
        @click="downloadSample"
        :disabled="isGenerating"
    >
      <i class="fas fa-download"></i>
      {{ isGenerating ? 'Generating...' : 'Download Sample Template' }}
      <b-spinner v-if="isGenerating" variant="light" small class="ms-2"></b-spinner>
    </button>

    <!-- File Upload Section -->
    <div class="mt-3">
      <label :for="inputId" class="form-label">{{ uploadLabel }}</label>
      <input
          type="file"
          :ref="inputId"
          :id="inputId"
          class="form-control"
          accept=".xls,.xlsx"
          @change="handleFileChange"
      />

      <!-- Error Display -->
      <div v-if="error" class="alert alert-danger mt-2">
        <i class="fas fa-exclamation-circle"></i> {{ error }}
      </div>

      <!-- Validation Errors -->
      <div v-if="validationErrors.length" class="mt-2">
        <div class="alert alert-warning">
          <strong>Validation Issues:</strong>
          <ul class="mb-0 mt-2">
            <li v-for="(error, index) in validationErrors" :key="index" class="text-danger">
              {{ error }}
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Preview Section -->
    <div v-if="previewData.length > 0 && validationErrors.length === 0" class="mt-4">
      <div class="card">
        <div class="card-header">
          <h6 class="mb-0">
            <i class="fas fa-eye"></i> Preview - First {{ Math.min(5, previewData.length) }} rows
          </h6>
        </div>
        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-bordered mb-0">
              <thead>
              <tr>
                <th v-for="(header, index) in previewHeaders" :key="index" class="fw-bold">
                  {{ header }}
                </th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(row, rowIndex) in previewData.slice(0, 5)" :key="rowIndex">
                <td v-for="(header, colIndex) in previewHeaders" :key="colIndex">
                  {{ row[header] || '' }}
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="card-footer">
          <!-- Total records line -->
          <div class="text-center mb-3">
            <strong>
              <i class="fas fa-check-circle text-success"></i> Total records to import: {{ previewData.length }}
            </strong>
          </div>
        </div>
      </div>
    </div>

    <!-- Action Buttons - Always visible -->
    <div class="mt-4">
      <div class="d-flex justify-content-end gap-2">
        <button
            v-if="previewData.length > 0 && validationErrors.length === 0"
            class="btn btn-warning"
            @click="confirmImport"
            :disabled="isImporting"
        >
          <i class="fas fa-upload"></i>
          {{ isImporting ? 'Importing...' : 'Import Data' }}
          <b-spinner v-if="isImporting" variant="light" small class="ms-2"></b-spinner>
        </button>
        <button
            class="btn btn-secondary"
            @click="$emit('close-modal')"
        >
          <i class="fa fa-times"></i> Close
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import * as XLSX from 'sheetjs-style';
import axios from 'axios';

export default {
  name: 'ExcelImport',
  props: {
    // Template fields definition
    templateFields: {
      type: Object,
      required: true,
    },
    // Sample data for template generation
    sampleData: {
      type: Array,
      default: () => []
    },
    // Filename for the sample template
    templateFilename: {
      type: String,
      default: 'sample_template.xlsx'
    },
    // Upload label
    uploadLabel: {
      type: String,
      default: 'Select Excel file to import:'
    },
    // Import endpoint URL
    importUrl: {
      type: String,
      required: true
    },
    // Required headers for validation
    requiredHeaders: {
      type: Array,
      default: () => []
    },
    // Custom validation function
    customValidation: {
      type: Function,
      default: null
    }
  },
  data() {
    return {
      isGenerating: false,
      isImporting: false,
      error: '',
      validationErrors: [],
      previewData: [],
      previewHeaders: [],
      selectedFile: null,
      inputId: `excel-input-${Math.random().toString(36).substr(2, 9)}`
    };
  },
  methods: {
    // Generate and download sample Excel template
    async downloadSample() {
      this.isGenerating = true;
      try {
        // Create workbook
        const wb = XLSX.utils.book_new();

        // Create worksheet from sample data or empty template
        let ws;
        if (this.sampleData.length > 0) {
          // Use provided sample data
          ws = XLSX.utils.json_to_sheet(this.sampleData);
        } else {
          // Create empty template with headers only
          const headers = Object.values(this.templateFields);
          ws = XLSX.utils.aoa_to_sheet([headers]);
        }

        // Apply styling to the worksheet
        this.styleWorksheet(ws);

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(wb, ws, 'Template');

        // Generate and download file
        XLSX.writeFile(wb, this.templateFilename);

      } catch (error) {
        this.error = `Failed to generate template: ${error.message}`;
      } finally {
        this.isGenerating = false;
      }
    },

    // Apply beautiful styling to worksheet
    styleWorksheet(ws) {
      const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');

      // Style header row
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
        if (!ws[cellAddress]) continue;

        ws[cellAddress].s = {
          fill: {
            patternType: "solid",
            fgColor: { rgb: "0069d9" }
          },
          font: {
            bold: true,
            color: { rgb: "FFFFFF" },
            sz: 12,
            name: "Calibri"
          },
          alignment: {
            horizontal: "center",
            vertical: "center",
            wrapText: false
          },
          border: {
            top: { style: "medium", color: { rgb: "1F4788" } },
            bottom: { style: "medium", color: { rgb: "1F4788" } },
            left: { style: "thin", color: { rgb: "1F4788" } },
            right: { style: "thin", color: { rgb: "1F4788" } }
          }
        };
      }

      // Style data rows with alternating colors
      for (let row = 1; row <= range.e.r; row++) {
        for (let col = range.s.c; col <= range.e.c; col++) {
          const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
          if (!ws[cellAddress]) continue;

          ws[cellAddress].s = {
            fill: {
              patternType: "solid",
              fgColor: { rgb: row % 2 === 0 ? "F8F9FA" : "FFFFFF" }
            },
            font: {
              name: "Calibri",
              sz: 11,
              color: { rgb: "212529" }
            },
            border: {
              top: { style: "thin", color: { rgb: "DEE2E6" } },
              bottom: { style: "thin", color: { rgb: "DEE2E6" } },
              left: { style: "thin", color: { rgb: "DEE2E6" } },
              right: { style: "thin", color: { rgb: "DEE2E6" } }
            },
            alignment: {
              vertical: "center",
              horizontal: "left",
              wrapText: false
            }
          };

          // Special styling for numeric columns
          if (ws[cellAddress] && ws[cellAddress].v && !isNaN(ws[cellAddress].v)) {
            ws[cellAddress].s.alignment.horizontal = "right";
            ws[cellAddress].s.numFmt = "#,##0"; // Thousand separator
          }
        }
      }

      // Set dynamic column widths based on content
      const colWidths = [];
      for (let col = range.s.c; col <= range.e.c; col++) {
        let maxWidth = 12; // Minimum width

        // Check header width
        const headerCell = XLSX.utils.encode_cell({ r: 0, c: col });
        if (ws[headerCell] && ws[headerCell].v) {
          maxWidth = Math.max(maxWidth, String(ws[headerCell].v).length + 3);
        }

        // Check data width (sample first few rows)
        for (let row = 1; row <= Math.min(range.e.r, 20); row++) {
          const dataCell = XLSX.utils.encode_cell({ r: row, c: col });
          if (ws[dataCell] && ws[dataCell].v) {
            maxWidth = Math.max(maxWidth, String(ws[dataCell].v).length + 2);
          }
        }

        colWidths.push({ wch: Math.min(maxWidth, 35) }); // Max 35 characters
      }
      ws['!cols'] = colWidths;

    },

    // Handle file selection and preview
    async handleFileChange(event) {
      this.resetState();

      const files = event.target.files;
      if (!files || files.length === 0) return;

      const file = files[0];
      this.selectedFile = file;

      // Validate file type
      if (!this.isValidExcelFile(file)) {
        this.error = "Please select a valid Excel file (.xls or .xlsx)";
        return;
      }

      try {
        await this.parseAndPreview(file);
      } catch (error) {
        this.error = `Error reading file: ${error.message}`;
      }
    },

    // Check if file is valid Excel format
    isValidExcelFile(file) {
      const validTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];
      return validTypes.includes(file.type) || file.name.match(/\.(xlsx?|xls)$/i);
    },

    // Parse Excel file and generate preview
    async parseAndPreview(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });

            // Get first worksheet
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];

            // Convert to JSON
            const jsonData = XLSX.utils.sheet_to_json(worksheet);

            if (jsonData.length === 0) {
              this.error = "The Excel file appears to be empty or has no valid data.";
              return reject(new Error("Empty file"));
            }

            // Extract headers from first row
            this.previewHeaders = Object.keys(jsonData[0]);
            this.previewData = jsonData;

            // Validate the data
            this.validateData();

            resolve();
          } catch (error) {
            reject(error);
          }
        };

        reader.onerror = () => reject(new Error("Failed to read file"));
        reader.readAsArrayBuffer(file);
      });
    },

    // Validate imported data
    validateData() {
      this.validationErrors = [];

      // Check required headers
      if (this.requiredHeaders.length > 0) {
        const missingHeaders = this.requiredHeaders.filter(
            header => !this.previewHeaders.includes(header)
        );

        if (missingHeaders.length > 0) {
          this.validationErrors.push(
              `Missing required columns: ${missingHeaders.join(', ')}`
          );
        }
      }

      // Run custom validation if provided
      if (this.customValidation && typeof this.customValidation === 'function') {
        try {
          const customErrors = this.customValidation(this.previewData, this.previewHeaders);
          if (customErrors && Array.isArray(customErrors)) {
            this.validationErrors.push(...customErrors);
          }
        } catch (error) {
          this.validationErrors.push(`Validation error: ${error.message}`);
        }
      }

      // Validate each row
      this.previewData.forEach((row, index) => {
        const rowNum = index + 2; // +2 because Excel is 1-indexed and we have headers

        // Check for empty required fields
        this.requiredHeaders.forEach(header => {
          if (!row[header] || String(row[header]).trim() === '') {
            this.validationErrors.push(`Row ${rowNum}: ${header} is required`);
          }
        });
      });
    },

    // Import data to backend
    async confirmImport() {
      if (this.validationErrors.length > 0) {
        this.error = "Please fix validation errors before importing.";
        return;
      }

      this.isImporting = true;
      try {
        const formData = new FormData();
        formData.append('file', this.selectedFile);

        const response = await axios.post(this.importUrl, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (response.status === 200) {
          this.$emit('import-success', {
            message: response.data.message || 'Data imported successfully!',
            data: this.previewData
          });
          this.resetState();
        }
      } catch (error) {
        const errorMessage = error.response?.data?.error || error.message || 'Import failed';
        this.$emit('import-error', errorMessage);
        this.error = errorMessage;
      } finally {
        this.isImporting = false;
      }
    },

    // Reset component state
    resetState() {
      this.error = '';
      this.validationErrors = [];
      this.previewData = [];
      this.previewHeaders = [];
      this.selectedFile = null;
    }
  }
};
</script>

<style scoped>
.form-control {
  border-radius: 6px;
  border: 1px solid #ced4da;
  padding: 8px 12px;
}

.form-control:focus {
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0,123,255,0.25);
}

.card {
  border-radius: 6px;
}

.fw-bold {
  font-weight: bold;
}

.gap-2 > * + * {
  margin-left: 0.5rem;
}
</style>